/**
 * Le catalogue de la facturation Oxpium : plans, prix, crédits par requête,
 * add-ons. Partagé mot pour mot entre l'interface et systems
 * (scripts/check-shared-sync.sh) : c'est la seule source des chiffres côté
 * code. Stripe porte les mêmes prix (`systems/scripts/stripe-setup.ts`, clés
 * `lookup_key`), et la migration 057 recopie le seul plan Gratuit
 * (`billing_free_config()`).
 *
 * Référence produit : la doc « How Oxpium pricing works »
 * (https://claude.ai/code/artifact/aa696291-8e16-44f2-92a4-3640098d8a63).
 *
 * Fonctions pures uniquement : ni I/O, ni import d'une app.
 */

/** Prix d'un crédit inclus dans un abonnement, partout, à tout volume. */
export const CREDIT_PRICE_EUR = 0.05;
/** Majoration du top-up : un crédit au-delà du plan coûte 0,065 €. */
export const TOPUP_MARKUP = 0.3;
export const TOPUP_CREDIT_PRICE_EUR = round(CREDIT_PRICE_EUR * (1 + TOPUP_MARKUP), 4);
/** Remise de la facturation annuelle. */
export const YEARLY_DISCOUNT = 0.2;
/** Conversion des coûts fournisseurs (en dollars) : la doc compte $1 ≈ 0,87 €. */
export const USD_TO_EUR = 0.87;

/** Bornes du nombre de crédits mensuels choisi à la souscription. */
export const MAX_CREDITS = 100_000;
export const CREDITS_STEP = 100;

export const PLAN_IDS = ["free", "essential", "pro", "ultra", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/** Les plans qu'un client souscrit seul, par Stripe Checkout. */
export const SELF_SERVE_PLAN_IDS = ["essential", "pro", "ultra"] as const;
export type SelfServePlanId = (typeof SELF_SERVE_PLAN_IDS)[number];

export const BILLING_INTERVALS = ["month", "year"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/** Ce qu'un plan débloque, au-delà des crédits. */
export const PLAN_FEATURES = ["csvExport", "crmSync", "api", "mcp"] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

/** Les plafonds d'un plan ; absent = illimité. */
export type PlanLimits = {
  /** Membres de l'org, invitations en attente comprises. */
  users?: number;
  /** Campagnes de l'org (toutes, archivées comprises). */
  campaigns?: number;
};

/**
 * Achats d'add-ons (boîtes, domaines, numéros) : `stripe` = payés par
 * l'abonnement, `manual` = autorisés et facturés hors Stripe (accord
 * Entreprise), `none` = fermés.
 */
export type PurchaseMode = "stripe" | "manual" | "none";

export type PlanDefinition = {
  id: PlanId;
  /** Ordre des plans, pour dire si un changement monte ou descend. */
  rank: number;
  /** Abonnement de base, par mois, hors crédits (HT). */
  baseMonthlyEur: number;
  /** Crédits mensuels minimum (plans payants) ; le Gratuit en a un nombre fixe. */
  minCredits: number;
  /** Budget mensuel « usage Ox Agent », en coût réel (EUR) ; null = illimité. */
  agentBudgetEur: number | null;
  /** Les crédits non utilisés passent au mois suivant, sans limite. */
  rollover: boolean;
  limits: PlanLimits;
  features: Record<PlanFeature, boolean>;
};

const ALL_FEATURES: Record<PlanFeature, boolean> = {
  csvExport: true,
  crmSync: true,
  api: true,
  mcp: true,
};

export const PLANS: Record<PlanId, PlanDefinition> = {
  free: {
    id: "free",
    rank: 0,
    baseMonthlyEur: 0,
    minCredits: 100,
    agentBudgetEur: 3,
    rollover: false,
    limits: { users: 1, campaigns: 1 },
    features: { csvExport: false, crmSync: false, api: false, mcp: false },
  },
  essential: {
    id: "essential",
    rank: 1,
    baseMonthlyEur: 55,
    minCredits: 500,
    agentBudgetEur: 28.88,
    rollover: false,
    limits: {},
    features: { csvExport: true, crmSync: false, api: false, mcp: false },
  },
  pro: {
    id: "pro",
    rank: 2,
    baseMonthlyEur: 165,
    minCredits: 1500,
    agentBudgetEur: 86.63,
    rollover: false,
    limits: {},
    features: ALL_FEATURES,
  },
  ultra: {
    id: "ultra",
    rank: 3,
    baseMonthlyEur: 330,
    minCredits: 3000,
    agentBudgetEur: 231,
    rollover: true,
    limits: {},
    features: ALL_FEATURES,
  },
  // Accord sur mesure, posé à la main (admin) : rien n'est facturé par
  // Stripe, crédits et budget agent illimités sauf réglage contraire.
  enterprise: {
    id: "enterprise",
    rank: 4,
    baseMonthlyEur: 0,
    minCredits: 0,
    agentBudgetEur: null,
    rollover: false,
    limits: {},
    features: ALL_FEATURES,
  },
};

export function isSelfServePlan(plan: string): plan is SelfServePlanId {
  return (SELF_SERVE_PLAN_IDS as readonly string[]).includes(plan);
}

/** Ce qu'un client choisit : un plan payant, un nombre de crédits, un rythme. */
export type PlanSelection = {
  plan: SelfServePlanId;
  credits: number;
  interval: BillingInterval;
};

/** Le plan tel qu'il s'applique à une org (ce que la base retient). */
export type PlanSelectionState = {
  plan: PlanId;
  credits: number;
  interval: BillingInterval | null;
};

/** Un nombre de crédits valide pour ce plan : entier, au pas, entre le minimum et le maximum. */
export function isValidCredits(plan: SelfServePlanId, credits: number): boolean {
  return (
    Number.isInteger(credits) &&
    credits >= PLANS[plan].minCredits &&
    credits <= MAX_CREDITS &&
    credits % CREDITS_STEP === 0
  );
}

/**
 * Prix mensuel HT d'un abonnement : base + crédits × 0,05 €, −20 % en
 * annuel (le prix annuel facturé vaut 12 fois ce montant).
 */
export function monthlyPriceEur(selection: PlanSelection): number {
  const full = PLANS[selection.plan].baseMonthlyEur + selection.credits * CREDIT_PRICE_EUR;
  return round(selection.interval === "year" ? full * (1 - YEARLY_DISCOUNT) : full, 2);
}

/** Montant facturé à chaque échéance (un mois ou un an), HT. */
export function invoicedPriceEur(selection: PlanSelection): number {
  const monthly = monthlyPriceEur(selection);
  return round(selection.interval === "year" ? monthly * 12 : monthly, 2);
}

/**
 * Quand un changement de plan prend effet :
 *   - `now` : tout monte ou rien ne baisse (plan, crédits), ou mensuel → annuel ;
 *     appliqué tout de suite, prorata facturé, différence de crédits offerte ;
 *   - `renewal` : quoi que ce soit baisse ; appliqué à la fin du mois en cours ;
 *   - `unsupported` : annuel → mensuel (engagement annuel), sur demande ; et
 *     mensuel → annuel avec une baisse : passer d'abord à l'annuel, baisser
 *     ensuite (au renouvellement annuel), un changement d'intervalle sans
 *     prorata, Stripe ne facturerait plus rien jusqu'à l'anniversaire ;
 *   - `none` : rien ne change.
 */
export function planChangeTiming(
  current: PlanSelectionState,
  next: PlanSelection,
): "now" | "renewal" | "unsupported" | "none" {
  if (current.interval === "year" && next.interval === "month") return "unsupported";
  const rankDelta = PLANS[next.plan].rank - PLANS[current.plan].rank;
  const creditsDelta = next.credits - current.credits;
  const intervalUp = current.interval === "month" && next.interval === "year";
  if (rankDelta === 0 && creditsDelta === 0 && !intervalUp) return "none";
  if (rankDelta < 0 || creditsDelta < 0) return intervalUp ? "unsupported" : "renewal";
  return "now";
}

/**
 * Crédits débités par requête. Règle de la doc : un crédit vendu 0,05 € ne
 * couvre jamais plus de 0,035 € de coût fournisseur (≥ 30 % de marge).
 * SIRENE et Boond sont gratuits ; l'envoi d'emails aussi.
 */
export const CREDIT_COSTS = {
  email_found: 1,
  phone_found: 10,
  // Une page de résultats Google (Serper), par l'agent comme par un script ;
  // avec un schéma, les pages lues sont débitées en plus (`web_page`).
  web_search: 0.05,
  web_page: 0.05,
  places_page: 0.1,
  places_filter_base: 0.5,
  places_filter_per_place: 0.01,
  serp_query: 0.05,
  company_search_page: 0.5,
  // ReverseContact facture la recherche de personnes au profil livré depuis
  // le 2026-10-13 (1 crédit fournisseur chacun), plus à la page.
  people_search_profile: 0.5,
  job_offer: 0.15,
  job_post: 1,
  company_technologies: 3,
  call_minute: 0.7,
} as const;

/** Ce que débite une ligne du registre de crédits (`billing_credit_charges.kind`). */
export const CREDIT_KINDS = [
  "email_found",
  "phone_found",
  "web_search",
  "web_page",
  "places_search",
  "places_filter",
  "serp_search",
  "company_search",
  "people_search",
  "job_offers",
  "job_posts",
  "company_technologies",
  "call_minutes",
] as const;
export type CreditKind = (typeof CREDIT_KINDS)[number];

/**
 * Ce qui compte dans l'usage Ox Agent (`billing_agent_usage.kind`). La
 * première analyse d'un appel (transcription et résumé, automatique) est
 * comprise dans le prix de la minute d'appel ; une relance à la main
 * (« Relancer ») compte, en `call_analysis`. `transcription` : les audios
 * joints au chat.
 */
export const AGENT_USAGE_KINDS = [
  "chat",
  "automation",
  "enrichment_agent",
  "script_llm",
  "sandbox",
  "chat_title",
  "task_report",
  "transcription",
  "call_analysis",
] as const;
export type AgentUsageKind = (typeof AGENT_USAGE_KINDS)[number];

export const ADDON_IDS = ["mailbox", "domain", "phone_number"] as const;
export type AddonId = (typeof ADDON_IDS)[number];

/** Les add-ons, payés à l'unité en plus de tout plan payant ; aucun n'est inclus. */
export const ADDONS: Record<AddonId, { priceEur: number; interval: BillingInterval }> = {
  mailbox: { priceEur: 6, interval: "month" },
  domain: { priceEur: 15, interval: "year" },
  phone_number: { priceEur: 20, interval: "month" },
};

/** Pourcentage entier d'une jauge, borné à [0, 100] ; null si le plafond est illimité. */
export function gaugePercent(used: number, limit: number | null): number | null {
  if (limit === null) return null;
  if (limit <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, Math.floor((used / limit) * 100)));
}

/** Crédits, arrondis comme le registre (3 décimales). */
export function roundCredits(value: number): number {
  return round(value, 3);
}

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
